/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { ListViewProps } from '@airasia-phoenix/react-native/components/ListView';
import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import type {
  TextFieldProps,
  TextFieldRefs,
} from '@airasia-phoenix/react-native/components/TextFieldView/types';
import {
  autoCapitalizeValues,
  DropDownPosition,
} from '@airasia-phoenix/react-native/components/TextFieldView/types';
import React, { type Ref } from 'react';
import { StyleSheet, View } from 'react-native';
import { EMAIL_DOMAIN_FIELD_TEST_ID } from '../EmailDomainDismissOverlayView';

const EMAIL_DOMAIN_DROPDOWN_OFFSET = 52;

export type EmailDomainFieldViewProps = {
  emailFieldProps: Pick<TextFieldProps, 'label'> &
    Partial<Omit<TextFieldProps, 'label'>>;
  emailFieldRef?: Ref<TextFieldRefs>;
  email?: string;
  emailErrorMessage: string;
  onEmailChange: (text: string) => void;
  showEmailDomainDropdown?: boolean;
  emailDomainDropdownData?: ListViewProps[];
  emailDomainDropdownHeight?: number;
  onEmailDomainSelect?: (item: ListViewProps) => void;
  onEmailFocusChange?: (focused: boolean) => void;
  emailClearEndIcon?: TextFieldProps['endIcon'];
  onEmailEndIconPress?: () => void;
};

/** Email input with domain suggestions, shared by login and sign-up. */
export function EmailDomainFieldView({
  emailFieldProps,
  emailFieldRef,
  email,
  emailErrorMessage,
  onEmailChange,
  showEmailDomainDropdown = false,
  emailDomainDropdownData,
  emailDomainDropdownHeight,
  onEmailDomainSelect,
  onEmailFocusChange,
  emailClearEndIcon,
  onEmailEndIconPress,
}: EmailDomainFieldViewProps) {
  return (
    <View
      testID={EMAIL_DOMAIN_FIELD_TEST_ID}
      style={styles.emailFieldContainer}
    >
      <TextFieldView
        ref={emailFieldRef}
        {...emailFieldProps}
        testID="email-input"
        value={email}
        isError={!!emailErrorMessage && !showEmailDomainDropdown}
        errorMessage={showEmailDomainDropdown ? '' : emailErrorMessage}
        onChangeText={onEmailChange}
        disabled={emailFieldProps.disabled ?? false}
        keyboardType="email-address"
        autoCapitalize={autoCapitalizeValues.NONE}
        autoCorrect={false}
        showDropDownView={showEmailDomainDropdown}
        dropDownViewData={emailDomainDropdownData}
        dropDownViewHeight={emailDomainDropdownHeight}
        dropDownPosition={DropDownPosition.BOTTOM}
        showDropDownDivider={true}
        dropDownOffset={EMAIL_DOMAIN_DROPDOWN_OFFSET}
        onDropDownViewItemPress={onEmailDomainSelect}
        endIcon={emailClearEndIcon}
        onEndIconPress={onEmailEndIconPress}
        isFocused={onEmailFocusChange}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  emailFieldContainer: {
    zIndex: 2,
  },
});
